import { useEffect, useRef, type RefObject } from 'react';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Open dialogs, innermost last, for the Tab trap: only the top one keeps Tab inside itself.
 * Esc goes through `useTopmostEscape`, the app's one Esc stack, so dialogs, menus and the BAB
 * overlays never disagree about which layer a keypress closes.
 */
const stack: symbol[] = [];

/** Put focus back on `el` — unless something else already took it (a dialog that just opened). */
export function restoreFocus(el: HTMLElement | null): void {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    if (el && document.contains(el)) el.focus();
}

/**
 * Everything a modal needs so a keyboard user is never left behind it (design audit
 * 2026-10-07, S1-03): focus moves INTO the dialog when it opens, Tab and Shift+Tab stay inside,
 * Esc closes, and focus returns to whatever opened it.
 *
 * The panel itself receives the first focus (it needs `tabIndex={-1}`) unless `initialFocus`
 * points somewhere: a dialog that opens with a button pre-focused invites the Enter that was
 * already on its way somewhere else. The pattern is `whats-new/WhatsNewDialog.tsx`'s, as a hook
 * so every modal of the app can share it rather than each re-deriving it.
 *
 * Pair it with `role="dialog"`, `aria-modal="true"` and `aria-labelledby` on the panel.
 */
export function useDialog(
    panelRef: RefObject<HTMLElement>,
    onClose: () => void,
    options?: { initialFocus?: RefObject<HTMLElement>; closeOnEscape?: boolean },
): void {
    const close = useRef(onClose);
    close.current = onClose;
    const initialFocus = options?.initialFocus;

    useTopmostEscape(options?.closeOnEscape ?? true, () => close.current());

    useEffect(() => {
        const me = Symbol('dialog');
        stack.push(me);
        const returnTo = document.activeElement as HTMLElement | null;
        (initialFocus?.current ?? panelRef.current)?.focus();

        const onKey = (e: KeyboardEvent) => {
            const panel = panelRef.current;
            if (!panel || stack[stack.length - 1] !== me || e.key !== 'Tab') return;
            const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(el => el.offsetParent !== null);
            if (items.length === 0) { e.preventDefault(); panel.focus(); return; }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && (active === first || active === panel)) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
            else if (!panel.contains(active)) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            stack.splice(stack.indexOf(me), 1);
            restoreFocus(returnTo);
        };
        // Mount/unmount only: the dialog's lifetime IS this effect's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
